import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Home } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../components/ui/use-toast';
import logo from '../../assets/bmp-logo-dark.png';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email') || '';
  
  const [email, setEmail] = useState(emailFromUrl);
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleResetPassword = async (e) => {
    e.preventDefault();

    // Validation
    if (!email || !otpCode || !newPassword || !confirmPassword) {
      toast({
        variant: "destructive",
        title: "All Fields Required",
        description: "Please fill in all fields",
      });
      return;
    }

    if (otpCode.length !== 6) {
      toast({
        variant: "destructive",
        title: "Invalid Code",
        description: "Reset code must be 6 digits",
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        variant: "destructive",
        title: "Password Too Short",
        description: "Password must be at least 8 characters",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords Don't Match",
        description: "Please ensure both passwords are identical",
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password/verify`, {
        email,
        otp_code: otpCode,
        new_password: newPassword
      });

      toast({
        title: "Password Reset Successful",
        description: "You can now log in with your new password",
      });

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/admin/login');
      }, 2000);

    } catch (error) {
      console.error('Password reset error:', error);
      toast({
        variant: "destructive",
        title: "Reset Failed",
        description: error.response?.data?.detail || "Invalid or expired code",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#023E8A] to-[#00B4D8] flex items-center justify-center px-4 relative">
      {/* Back to Homepage Button - Top Left */}
      <button
        onClick={() => window.location.href = '/'}
        className="absolute top-6 left-6 flex items-center gap-2 text-white hover:text-gray-200 transition-colors group"
      >
        <div className="p-2 rounded-lg bg-white/10 group-hover:bg-white/20 transition-colors">
          <Home className="w-5 h-5" />
        </div>
        <span className="font-medium hidden sm:inline">Back to Homepage</span>
      </button>

      {/* Language Switcher - Top Right */}
      <div className="absolute top-6 right-6">
        <div className="px-4 py-2 rounded-lg bg-white/10 text-white border border-white/20">
          EN
        </div>
      </div>

      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src={logo} alt="Grupo BMP" className="h-16 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#2B2D42]">Reset Password</h1>
            <p className="text-[#6C757D] mt-2">
              Enter the code sent to your email
            </p>
          </div>

          {/* Reset Password Form */}
          <form onSubmit={handleResetPassword} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Reset Code (6 digits)
              </label>
              <Input
                type="text"
                value={otpCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtpCode(value);
                }}
                placeholder="123456"
                required
                maxLength={6}
                className="w-full text-center text-2xl tracking-widest font-mono"
              />
              <p className="text-xs text-[#6C757D] mt-2">
                Check your email (including spam folder) for the 6-digit code
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                New Password
              </label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showNewPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-[#6C757D] mt-1">
                Minimum 8 characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00B4D8] hover:bg-[#0096c7] text-white py-6 text-base font-semibold transition-colors"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Resetting Password...</span>
                </div>
              ) : (
                'Reset Password'
              )}
            </Button>

            {/* Back to Login Link */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/admin/login')}
                className="text-sm text-[#6C757D] hover:text-[#2B2D42] font-medium transition-colors flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </button>
            </div>
          </form>

          {/* Help Text */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-[#023E8A] font-medium mb-1">
              💡 Need Help?
            </p>
            <p className="text-xs text-[#6C757D]">
              • Code expires in 10 minutes<br />
              • Check spam folder if you don't see the email<br />
              • Contact support if you need assistance
            </p>
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-[#6C757D] mt-6">
            Grupo BMP Admin Panel © 2025
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
